import { VFC, useState } from 'react'
import { usePersonaList } from 'src/api/persona'
import { Persona } from 'src/models/Persona'
import { LoginUser } from 'src/models/User'
import {
  _message_submenu,
  safetyOrange,
  softOrange,
  truewhite,
} from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import styled from 'styled-components'
import { ProfileEditorWrapper } from '../ProfileLayout'
import { Title, labelHoverEffectStyle } from '../formComponents'
import { PersonaSelectModal } from './PersonaSelectModal'

const FREE_PLAN_PERSONA_MAX_COUNT = 1
const PAID_PLAN_PERSONA_MAX_COUNT = 5
const createButtonCount = (personaCount: number, paidPlan?: boolean) => {
  if (paidPlan === undefined) return 0
  const personaMaxCount = paidPlan
    ? PAID_PLAN_PERSONA_MAX_COUNT
    : FREE_PLAN_PERSONA_MAX_COUNT

  if (Math.sign(personaMaxCount - personaCount) === -1) return 0

  return personaMaxCount - personaCount
}

const isCheckedPersona = (persona: Persona) => {
  if (persona.personaTypeOfBusinessIds.length) return true
  if (persona.personaEmployeeNumberIds.length) return true
  if (persona.personaCompanyBuyNeedOptions.length) return true
  if (persona.personaPrefectures.length) return true
  if (persona.personaPositions.length) return true
  if (persona.personaCapitals.length) return true
  if (persona.isListedCompany) return true
  if (persona.personaBusinessObjects.length) return true
  if (persona.personaFoundedAts.length) return true
  if (persona.personaAdminAges.length) return true

  return false
}

type CompanyPersonasIndexPageProps = {
  user: LoginUser
}

export const CompanyPersonasIndexPage: VFC<CompanyPersonasIndexPageProps> = ({
  user,
}) => {
  const personaList = usePersonaList()
  const personas = personaList.data?.personas || []
  const [modalOpen, setModalOpen] = useState(false)
  const [personaId, setPersonaId] = useState(0) // NOTE: ペルソナ作成の場合は0とする
  const description = `ペルソナ企業を登録すると、プロフィール内の「こんな人に会いたい」の中に表示されあなたのターゲットに近い人から「つながりたい」や「メッセージ」が届きやすくなります。${
    user.plan.paid ? '有料会員は５件登録できます。' : ''
  } `

  return (
    <ProfileEditorWrapper user={user} currentPath="ペルソナ登録">
      <Title title={'ペルソナ登録'} description={description} />
      {/* NOTE: ペルソナが作成された順に表示する */}
      <PersonaList>
        {personas.map((persona, i) => (
          <PersonaContainer key={i}>
            <PersonaNumber>{`ペルソナ ${i + 1}`}</PersonaNumber>
            {isCheckedPersona(persona) ? (
              <DisplayAndEditPersonas
                persona={persona}
                setModalOpen={
                  user.plan.freeAndPartner ? () => {} : setModalOpen
                }
                setPersonaId={setPersonaId}
                notPaid={user.plan.freeAndPartner}
              />
            ) : (
              <CreatePersona
                personaId={persona.id}
                setModalOpen={
                  user.plan.freeAndPartner ? () => {} : setModalOpen
                }
                setPersonaId={setPersonaId}
                notPaid={user.plan.freeAndPartner}
              />
            )}
          </PersonaContainer>
        ))}
      </PersonaList>
      {/* NOTE: どの番号のボタンでペルソナを作成しても、ペルソナが作成された順に表示する */}
      <CreatePersonaDiv>
        {[
          ...Array(createButtonCount(personas.length, user.company.paidPlan)),
        ].map((_, index) => {
          return (
            <PersonaContainer key={index}>
              <PersonaNumber>{`ペルソナ ${
                index + 1 + personas.length
              }`}</PersonaNumber>
              <CreatePersona
                personaId={0}
                setModalOpen={setModalOpen}
                setPersonaId={setPersonaId}
                notPaid={user.plan.freeAndPartner}
              />
            </PersonaContainer>
          )
        })}
      </CreatePersonaDiv>
      {modalOpen && (
        <PersonaSelectModal
          close={() => setModalOpen(false)}
          personaId={personaId}
        />
      )}
    </ProfileEditorWrapper>
  )
}

const CreatePersona: VFC<{
  personaId: number
  setModalOpen: (modalOpen: boolean) => void
  setPersonaId: (personaId: number) => void
  notPaid: boolean
}> = ({ personaId, setModalOpen, setPersonaId, notPaid }) => {
  return (
    <SettingDiv blur={notPaid}>
      <SettingButton
        type={'button'}
        onClick={() => {
          setModalOpen(true)
          if (!notPaid) setPersonaId(personaId)
        }}
      >
        {notPaid ? 'ペルソナは有料会員のみ設定出来ます' : '＋ 設定する'}
      </SettingButton>
    </SettingDiv>
  )
}

const DisplayAndEditPersonas: VFC<{
  persona: Persona
  setModalOpen: (modalOpen: boolean) => void
  setPersonaId: (personaId: number) => void
  notPaid: boolean
}> = ({ persona, setModalOpen, setPersonaId, notPaid }) => {
  return (
    <EditButton
      onClick={() => {
        setModalOpen(true)
        if (!notPaid) setPersonaId(persona.id)
      }}
    >
      <LabelsDiv notPaid={notPaid}>
        {persona.personaTypeOfBusinesses.length > 0 && (
          <>
            <ModalHeading>業種</ModalHeading>
            {persona.personaTypeOfBusinesses.map((type) => (
              <ConfirmLabel key={type.typeOfBusiness?.name || 'unknown'}>
                {type.typeOfBusiness?.name || '不明'}
              </ConfirmLabel>
            ))}
          </>
        )}
        {persona.personaEmployeeNumbers.length > 0 && (
          <>
            <ModalHeading>従業員数</ModalHeading>
            {persona.personaEmployeeNumbers.map((type) => (
              <ConfirmLabel key={type.employeeNumber?.name || 'unknown'}>
                {type.employeeNumber?.name || '不明'}
              </ConfirmLabel>
            ))}
          </>
        )}
        {persona.personaCompanyBuyNeedOptions.length > 0 && (
          <>
            <ModalHeading>ニーズ</ModalHeading>
            {persona.personaCompanyBuyNeedOptions.map((type) => (
              <ConfirmLabel key={type.companyBuyNeedOption?.name || 'unknown'}>
                {type.companyBuyNeedOption?.name || '不明'}
              </ConfirmLabel>
            ))}
          </>
        )}
        {persona.personaPrefectures.length > 0 && (
          <>
            <ModalHeading>所在地</ModalHeading>
            {persona.personaPrefectures.map((type) => (
              <ConfirmLabel key={type.prefecture?.name || 'unknown'}>
                {type.prefecture?.name || '不明'}
              </ConfirmLabel>
            ))}
          </>
        )}
        {persona.personaCapitals.length > 0 && (
          <>
            <ModalHeading>資本金</ModalHeading>
            {persona.personaCapitals.map((type) => (
              <ConfirmLabel key={type?.name || 'unknown'}>
                {type?.name || '不明'}
              </ConfirmLabel>
            ))}
          </>
        )}
        {persona.personaPositions.length > 0 && (
          <>
            <ModalHeading>役職</ModalHeading>
            {persona.personaPositions.map((type) => (
              <ConfirmLabel key={type?.name || 'unknown'}>
                {type?.name || '不明'}
              </ConfirmLabel>
            ))}
          </>
        )}
        {persona.isListedCompany !== null && (
          <>
            <ModalHeading>上場・未上場</ModalHeading>
            <ConfirmLabel>
              {persona.isListedCompany ? '上場' : '未上場'}
            </ConfirmLabel>
          </>
        )}
        {persona.personaBusinessObjects.length > 0 && (
          <>
            <ModalHeading>商材</ModalHeading>
            {persona.personaBusinessObjects.map((type) => (
              <ConfirmLabel key={type.businessObject?.name || 'unknown'}>
                {type.businessObject?.name || '不明'}
              </ConfirmLabel>
            ))}
          </>
        )}
        {persona.personaFoundedAts.length > 0 && (
          <>
            <ModalHeading>設立年数</ModalHeading>
            {persona.personaFoundedAts.map((type) => (
              <ConfirmLabel key={type.foundedAt?.name || 'unknown'}>
                {type.foundedAt?.name || '不明'}
              </ConfirmLabel>
            ))}
          </>
        )}
        {persona.personaAdminAges.length > 0 && (
          <>
            <ModalHeading>決裁者の年齢</ModalHeading>
            {persona.personaAdminAges.map((type) => (
              <ConfirmLabel key={type.adminAge?.name || 'unknown'}>
                {type.adminAge?.name || '不明'}
              </ConfirmLabel>
            ))}
          </>
        )}
      </LabelsDiv>
    </EditButton>
  )
}

const PersonaList = styled.div`
  width: 700px;
  margin: 40px auto 0px;
  padding: 0 10px;
`

const CreatePersonaDiv = styled.div`
  width: 700px;
  margin: 0px auto 40px;
  padding: 0 10px;
`

const EditButton = styled.div`
  width: 100%;
  cursor: pointer;
`

const LabelsDiv = styled.div<{ notPaid: boolean }>`
  padding: 16px;
  border: 1px dashed ${safetyOrange};
  border-radius: 5px;

  ${({ notPaid }) =>
    notPaid ? `--blur-text: 'ペルソナは有料会員のみ設定出来ます';` : ''}
  ${labelHoverEffectStyle};
`

const PersonaNumber = styled.div`
  margin-bottom: 10px;
  font-size: 14px;
  color: ${_message_submenu};
  font-weight: ${fontWeightBold};
`

const PersonaContainer = styled.div`
  width: 100%;
  margin: 0px auto 24px;
  border-radius: 5px;
`

const SettingDiv = styled.div<{ blur: boolean }>`
  width: 100%;
  border-radius: 5px;
  padding: 20px 15px;
  text-align: center;
  position: relative;
  ${({ blur }) =>
    blur
      ? `
      &::after {
        border: dashed 1px ${safetyOrange};
        background: ${softOrange};
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        filter: blur(3.5px);
        background: inherit;
        border: dashed 1px ${safetyOrange};
        background: ${softOrange};
        z-index: -1;
      }
    `
      : `
      border: dashed 1px ${safetyOrange};
      background: ${softOrange};
    `};
`

const SettingButton = styled.button`
  margin: auto;
  font-weight: ${fontWeightBold};
  border: solid 1px;
  border-radius: 5px;
  padding: 10px 24px;
  color: ${safetyOrange};
  background: ${truewhite};
`

const ModalHeading = styled.p`
  margin: 8px 0 4px;
  font-size: 12px;
  color: ${_message_submenu};
  font-weight: ${fontWeightBold};
`

const ConfirmLabel = styled.span`
  padding: 4px 16px;
  border: 1px solid ${safetyOrange};
  background: #fef6ef;

  border-radius: 16px;
  color: ${safetyOrange};
  font-size: 14px;
  font-weight: ${fontWeightBold};
  line-height: 3;
  margin-right: 10px;
  margin-bottom: 4px;
  white-space: nowrap;
`
